/* eslint-disable react/no-unescaped-entities */
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Check, X, Zap, Target, Wallet, Dumbbell, Salad,
  ShieldCheck, Clock, User, MessageSquare, Sparkles,
} from 'lucide-react';
import { SEOHead } from '../hooks/usePageSEO';
import { trackEvent } from '../utils/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS_TOTAL = 8;

const initialForm = {
  motivation: 7,
  objectif: '',
  budget: '',
  seances: '',
  nutrition: '',
  engagement_rdv: false,
  timing: '',
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  city: '',
  obstacle: '',
};

const OBJECTIFS = [
  { key: 'perte_gras', label: 'Perdre du gras', icon: '🔥' },
  { key: 'prise_muscle', label: 'Prendre du muscle', icon: '💪' },
  { key: 'douleurs_dos', label: 'Soulager mes douleurs de dos', icon: '🩹' },
  { key: 'reprise', label: 'Reprise en forme (post-pause/blessure)', icon: '🌱' },
  { key: 'autre', label: 'Autre objectif', icon: '✨' },
];

const BUDGETS = [
  { key: 'moins_150', label: 'Moins de 150 CHF/mois', hint: 'Discovery' },
  { key: '150_300', label: '150 – 300 CHF / mois', hint: 'Zone Pack Transformation 🎯' },
  { key: '300_600', label: '300 – 600 CHF / mois', hint: 'Zone VIP Hybrid 👑' },
  { key: 'plus_600', label: 'Plus de 600 CHF / mois', hint: 'Premium 🚀' },
];

const SEANCES = [
  { key: '0_1', label: '0 – 1 séance / semaine', hint: 'Difficile pour transformer' },
  { key: '2', label: '2 séances / semaine', hint: 'Minimum recommandé' },
  { key: '3_4', label: '3 – 4 séances / semaine', hint: 'Le sweet spot 🎯' },
  { key: '5_plus', label: '5+ séances / semaine', hint: 'Engagement maximal' },
];

const NUTRITION_CHOICES = [
  { key: 'non', label: 'Non, uniquement le sport', hint: '⚠️ Résultats limités' },
  { key: 'compromis', label: 'Oui, avec quelques compromis', hint: 'Approche équilibrée' },
  { key: 'oui_100', label: 'Oui, à 100%', hint: 'Résultats accélérés 🚀' },
];

const TIMING_CHOICES = [
  { key: 'cette_semaine', label: 'Cette semaine', hint: '🔥 Prêt(e) à démarrer' },
  { key: 'ce_mois', label: 'Ce mois', hint: 'Motivé(e)' },
  { key: '2_3_mois', label: 'Dans 2-3 mois', hint: 'Je prépare' },
  { key: 'pas_presse', label: 'Pas pressé(e)', hint: 'Je me renseigne' },
];

const PostulerCoachingPage = () => {
  const [searchParams] = useSearchParams();
  const parcoursType = searchParams.get('type') === 'hybride' ? 'hybride' : 'online';
  const parcoursLabel = parcoursType === 'hybride' ? 'Coaching Hybride' : 'Coaching 100% en ligne';

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent('lead_qualification_start', { parcours: parcoursType });
  }, [parcoursType]);

  const progress = useMemo(() => Math.round(((step + 1) / STEPS_TOTAL) * 100), [step]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canContinue = useMemo(() => {
    switch (step) {
      case 0: return form.motivation >= 1 && form.motivation <= 10;
      case 1: return !!form.objectif;
      case 2: return !!form.budget;
      case 3: return !!form.seances;
      case 4: return !!form.nutrition;
      case 5: return form.engagement_rdv === true;
      case 6: return !!form.timing;
      case 7: return (
        form.first_name.trim().length >= 1 && form.last_name.trim().length >= 1 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && form.phone.trim().length >= 6
      );
      default: return false;
    }
  }, [step, form]);

  const next = () => {
    if (!canContinue) return;
    if (step < STEPS_TOTAL - 1) setStep((s) => s + 1);
    else submit();
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form, parcours_type: parcoursType, source_page: window.location.href };
      const res = await fetch(`${API}/api/leads/qualify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Erreur serveur, réessaie dans un instant.');
      const data = await res.json();
      setResult(data);
      trackEvent('lead_qualification_submit', { qualified: data.qualified, score: data.score, parcours: parcoursType });
      if (data.qualified) trackEvent('lead_qualified', { score: data.score, parcours: parcoursType });
      else trackEvent('lead_not_qualified', { score: data.score, parcours: parcoursType });
    } catch (e) {
      setError(e.message || 'Erreur inconnue');
    } finally {
      setSubmitting(false);
    }
  };

  // Écran de résultat
  if (result) {
    return <ResultScreen result={result} parcoursLabel={parcoursLabel} navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-900 text-white">
      <SEOHead
        title={`Postuler pour un appel avec Laurent — ${parcoursLabel}`}
        description="Un questionnaire rapide (2 min) pour qualifier ta candidature. Si ton profil correspond, tu accèdes directement à l'agenda pour réserver ton appel gratuit."
      />

      {/* Progress bar sticky */}
      <div className="sticky top-0 z-30 backdrop-blur bg-black/60 border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
              data-testid="postuler-close"
            >
              <X className="w-3.5 h-3.5" /> Quitter
            </button>
            <div className="text-xs font-semibold tracking-widest text-zinc-500">
              Étape {step + 1} / {STEPS_TOTAL}
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-brand-500 to-brand-400"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 md:py-16">
        <div className="mb-6 text-center">
          <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-widest bg-white/5 border border-white/10">
            BODY-UP · CANDIDATURE {parcoursLabel.toUpperCase()}
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35 }}
            data-testid={`postuler-step-${step}`}
          >
            {step === 0 && <Step0Motivation value={form.motivation} onChange={(v) => update('motivation', v)} />}
            {step === 1 && <Step1Objectif value={form.objectif} onChange={(v) => update('objectif', v)} />}
            {step === 2 && <Step2Budget value={form.budget} onChange={(v) => update('budget', v)} parcoursType={parcoursType} />}
            {step === 3 && <Step3Seances value={form.seances} onChange={(v) => update('seances', v)} />}
            {step === 4 && <Step4Nutrition value={form.nutrition} onChange={(v) => update('nutrition', v)} />}
            {step === 5 && <Step5Engagement value={form.engagement_rdv} onChange={(v) => update('engagement_rdv', v)} />}
            {step === 6 && <Step6Timing value={form.timing} onChange={(v) => update('timing', v)} />}
            {step === 7 && <Step7Contact form={form} update={update} />}
          </motion.div>
        </AnimatePresence>

        {error && (
          <div className="mt-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="mt-10 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="postuler-back"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!canContinue || submitting}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-bold bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-600 hover:to-brand-500 text-white shadow-lg shadow-brand-500/30 hover:shadow-brand-500/50 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.03] transition-all"
            data-testid="postuler-next"
          >
            {step === STEPS_TOTAL - 1 ? (submitting ? 'Envoi…' : 'Valider ma candidature') : 'Continuer'}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// -------- Sub-components (steps) -------- //

const StepShell = ({ icon: Icon, title, subtitle, children }) => (
  <div>
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500/20 to-brand-400/10 border border-brand-500/30 flex items-center justify-center mb-6">
      <Icon className="w-7 h-7 text-brand-400" />
    </div>
    <h1 className="text-3xl sm:text-4xl font-black mb-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {title}
    </h1>
    {subtitle && <p className="text-zinc-400 mb-8 text-base">{subtitle}</p>}
    {children}
  </div>
);

const RadioCard = ({ selected, onClick, label, hint, testId, icon }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left px-5 py-4 rounded-2xl border-2 transition-all ${
      selected
        ? 'border-brand-500 bg-brand-500/10 shadow-lg shadow-brand-500/20'
        : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        {icon && <span className="text-2xl">{icon}</span>}
        <div>
          <div className="font-semibold text-white">{label}</div>
          {hint && <div className="text-xs text-zinc-400 mt-0.5">{hint}</div>}
        </div>
      </div>
      {selected && <Check className="w-5 h-5 text-brand-400 shrink-0" />}
    </div>
  </button>
);

const Step0Motivation = ({ value, onChange }) => (
  <StepShell
    icon={Zap}
    title="Sur une échelle de 1 à 10, à quel point es-tu motivé(e) à transformer ton corps ?"
    subtitle="Sois vraiment honnête. Ma prestation demande de l'engagement — c'est ce qui fait la différence."
  >
    <div className="text-center mb-6">
      <div className="text-7xl font-black bg-gradient-to-br from-brand-500 to-brand-400 bg-clip-text text-transparent" data-testid="motivation-value">
        {value}
      </div>
      <div className="text-xs text-zinc-500 tracking-widest mt-2">SUR 10</div>
    </div>
    <input
      type="range"
      min="1"
      max="10"
      step="1"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="w-full accent-brand-500"
      data-testid="motivation-slider"
    />
    <div className="flex justify-between text-[10px] text-zinc-500 mt-2 tracking-widest">
      <span>PAS TROP</span>
      <span>MOYEN</span>
      <span>ULTRA MOTIVÉ 🔥</span>
    </div>
  </StepShell>
);

const Step1Objectif = ({ value, onChange }) => (
  <StepShell icon={Target} title="Quel est ton objectif principal ?" subtitle="Ça m'aide à préparer l'appel avec un plan concret pour toi.">
    <div className="space-y-3">
      {OBJECTIFS.map((o) => (
        <RadioCard
          key={o.key}
          selected={value === o.key}
          onClick={() => onChange(o.key)}
          label={o.label}
          icon={o.icon}
          testId={`objectif-${o.key}`}
        />
      ))}
    </div>
  </StepShell>
);

const Step2Budget = ({ value, onChange, parcoursType }) => (
  <StepShell
    icon={Wallet}
    title="Quel budget mensuel es-tu prêt(e) à investir pour transformer ton corps ?"
    subtitle={
      parcoursType === 'hybride'
        ? "Le Coaching Hybride est à 399 CHF/mois (6 mois d'engagement)."
        : "Le Coaching 100% en ligne est à 299 CHF/mois (6 mois d'engagement)."
    }
  >
    <div className="space-y-3">
      {BUDGETS.map((b) => (
        <RadioCard
          key={b.key}
          selected={value === b.key}
          onClick={() => onChange(b.key)}
          label={b.label}
          hint={b.hint}
          testId={`budget-${b.key}`}
        />
      ))}
    </div>
    <p className="text-xs text-zinc-500 mt-4">
      💡 Un vrai coach expert avec accompagnement personnalisé, ce n'est pas 29 CHF/mois. Investir dans ton corps, c'est investir dans ta santé et ton énergie pour les 30 prochaines années.
    </p>
  </StepShell>
);

const Step3Seances = ({ value, onChange }) => (
  <StepShell icon={Dumbbell} title="Combien de séances de sport peux-tu faire par semaine ?" subtitle="Soit réaliste par rapport à ton emploi du temps réel.">
    <div className="space-y-3">
      {SEANCES.map((s) => (
        <RadioCard
          key={s.key}
          selected={value === s.key}
          onClick={() => onChange(s.key)}
          label={s.label}
          hint={s.hint}
          testId={`seances-${s.key}`}
        />
      ))}
    </div>
  </StepShell>
);

const Step4Nutrition = ({ value, onChange }) => (
  <StepShell
    icon={Salad}
    title="Es-tu prêt(e) à ajuster ton alimentation sur les 6 prochains mois ?"
    subtitle="Sans nutrition alignée, même le meilleur programme d'entraînement donne 30% de résultats maximum. Sois honnête."
  >
    <div className="space-y-3">
      {NUTRITION_CHOICES.map((n) => (
        <RadioCard
          key={n.key}
          selected={value === n.key}
          onClick={() => onChange(n.key)}
          label={n.label}
          hint={n.hint}
          testId={`nutrition-${n.key}`}
        />
      ))}
    </div>
  </StepShell>
);

const Step5Engagement = ({ value, onChange }) => (
  <StepShell icon={ShieldCheck} title="L'engagement du rendez-vous" subtitle="Un point crucial avant de continuer.">
    <div className="p-5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-100 text-sm mb-5 leading-relaxed">
      <div className="font-bold text-base mb-2 text-amber-300">⚠️ Un no-show = blacklist définitif</div>
      Je bloque 30 minutes de mon agenda pour toi. Si tu ne te présentes pas à l'appel <strong>sans me prévenir au moins 24h avant</strong>, ton email est ajouté à ma liste noire et tu ne pourras plus jamais bénéficier d'un accompagnement Body-Up (même dans le futur).
      <br /><br />
      Ce n'est pas de la rigidité, c'est du respect : mon temps est aussi précieux que le tien.
    </div>
    <label className="flex items-start gap-3 p-4 rounded-2xl border-2 border-white/10 hover:border-white/30 cursor-pointer transition-all bg-white/5">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-5 h-5 rounded accent-brand-500 shrink-0 cursor-pointer"
        data-testid="engagement-checkbox"
      />
      <span className="text-sm text-white leading-relaxed">
        <strong>Oui, je m'engage à être présent(e)</strong> à l'appel Zoom à l'heure convenue. En cas d'imprévu, je préviens Laurent au moins 24h à l'avance.
      </span>
    </label>
  </StepShell>
);

const Step6Timing = ({ value, onChange }) => (
  <StepShell icon={Clock} title="Quand aimerais-tu démarrer ta transformation ?" subtitle="Pas de mauvaise réponse, ça m'aide à prioriser.">
    <div className="space-y-3">
      {TIMING_CHOICES.map((t) => (
        <RadioCard
          key={t.key}
          selected={value === t.key}
          onClick={() => onChange(t.key)}
          label={t.label}
          hint={t.hint}
          testId={`timing-${t.key}`}
        />
      ))}
    </div>
  </StepShell>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-semibold text-zinc-400 tracking-wider uppercase">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-white placeholder-zinc-500 transition-all"
  />
);

const Step7Contact = ({ form, update }) => (
  <StepShell icon={User} title="Tes coordonnées" subtitle="Pour te confirmer l'appel et t'envoyer les détails.">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Prénom *">
        <Input value={form.first_name} onChange={(e) => update('first_name', e.target.value)} placeholder="Marie" data-testid="contact-firstname" />
      </Field>
      <Field label="Nom *">
        <Input value={form.last_name} onChange={(e) => update('last_name', e.target.value)} placeholder="Dupont" data-testid="contact-lastname" />
      </Field>
      <Field label="Email *">
        <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="marie@exemple.ch" data-testid="contact-email" />
      </Field>
      <Field label="Téléphone / WhatsApp *">
        <Input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+41 79 123 45 67" data-testid="contact-phone" />
      </Field>
      <Field label="Ville (facultatif)">
        <Input value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Nyon, Genève, …" data-testid="contact-city" />
      </Field>
    </div>
    <div className="mt-6">
      <Field label={<span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Ce qui t'a empêché(e) d'atteindre ton objectif jusqu'à maintenant ? (facultatif)</span>}>
        <textarea
          value={form.obstacle}
          onChange={(e) => update('obstacle', e.target.value)}
          rows={4}
          placeholder="En 1-2 phrases, dis-moi ce qui te bloque : le temps, la motivation, l'alimentation, une blessure, l'absence de plan clair… Cette info m'aide à préparer un appel vraiment utile pour toi."
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 text-white placeholder-zinc-500 transition-all resize-none"
          data-testid="contact-obstacle"
        />
      </Field>
    </div>
    <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
      🔒 Tes données restent 100% confidentielles et ne sont jamais partagées. Elles servent uniquement à préparer notre appel et à te contacter.
    </p>
  </StepShell>
);

// -------- Résultat -------- //

const ResultScreen = ({ result, parcoursLabel, navigate }) => {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (result.qualified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-black to-zinc-900 text-white flex items-center justify-center px-4 py-16">
        <SEOHead title="Candidature acceptée — Réserver ton appel" description="Réserve ton appel gratuit de 30 min avec Laurent." />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-xl w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-green-400 flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-emerald-500/40">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>
          <div className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 mb-4">
            SCORE {result.score}/100 · CANDIDATURE ACCEPTÉE 🎯
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Bienvenue !<br />Ton profil correspond au <span className="bg-gradient-to-r from-brand-500 to-brand-400 bg-clip-text text-transparent">{parcoursLabel}</span>.
          </h1>
          <p className="text-zinc-400 mb-8 text-lg leading-relaxed">
            Prochaine étape : réserve un créneau de <strong className="text-white">30 min avec Laurent sur Zoom</strong>. Il aura déjà tes réponses sous les yeux — l'appel sera taillé sur mesure pour toi.
          </p>
          <a
            href={result.calendly_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('calendly_click', { source: 'lead_qualification_result', score: result.score })}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-600 hover:to-brand-500 text-white font-bold shadow-2xl shadow-brand-500/40 hover:scale-105 transition-transform"
            data-testid="result-calendly-cta"
          >
            <Sparkles className="w-5 h-5" /> Réserver mon appel avec Laurent
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-xs text-zinc-500 mt-6">
            Un email de confirmation te sera envoyé après avoir choisi ton créneau. Le lien Zoom est généré automatiquement.
          </p>
        </motion.div>
      </div>
    );
  }

  // Non qualifié
  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-900 text-white flex items-center justify-center px-4 py-16">
      <SEOHead title="Merci pour ta candidature" description="Notre programme premium n'est peut-être pas adapté maintenant. Découvre nos autres offres." />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl w-full text-center"
      >
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center mx-auto mb-6 border border-white/10">
          <MessageSquare className="w-10 h-10 text-zinc-300" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-black mb-4 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Merci pour ta candidature, mais…
        </h1>
        <p className="text-zinc-400 mb-6 text-base leading-relaxed">
          Notre programme premium demande un <strong className="text-white">niveau d'engagement (budget, séances, nutrition)</strong> qui ne semble pas correspondre à ta situation actuelle. Ce n'est ni un rejet ni un jugement — juste une question de <em>match</em>.
        </p>
        <p className="text-zinc-400 mb-8 text-base leading-relaxed">
          <strong className="text-white">Notre app Body-Up + Communauté à 29 CHF/mois</strong> est parfaite pour démarrer à ton rythme, sans engagement. Beaucoup de nos clients premium sont passés par là avant de nous rejoindre 🎯
        </p>
        <button
          type="button"
          onClick={() => navigate('/offres')}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-600 hover:to-brand-500 text-white font-bold shadow-lg hover:scale-105 transition-transform"
          data-testid="result-fallback-cta"
        >
          Découvrir nos offres à 29 CHF/mois <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-zinc-500 mt-6">
          Ton score : <strong>{result.score}/{100}</strong> · Seuil de qualification : {result.threshold}/100
        </p>
      </motion.div>
    </div>
  );
};

export default PostulerCoachingPage;
